// src/pages/Contact.tsx
import React, { useState, useRef, DragEvent } from "react";
import { PageHeader } from "../components/PageHeader.tsx";
import "./Contact.css";

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  category: string;
  websitePage: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  category?: string;
  websitePage?: string;
  message?: string;
  attachment?: string;
}

// 10 MB in bytes
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'application/zip',
  'application/x-zip-compressed'
];

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export const Contact: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    subject: "",
    category: "",
    websitePage: "",
    message: ""
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitMessage, setSubmitMessage] = useState("");

  // File attachment state
  const [attachment, setAttachment] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // File validation
  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size is 10 MB. Your file is ${formatFileSize(file.size)}.`;
    }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return 'Invalid file type. Allowed: images (JPG, PNG, GIF, WebP), PDF, Word docs, text, and ZIP files.';
    }
    return null;
  };

  // Handle file selection
  const handleFileSelect = (file: File) => {
    const error = validateFile(file);
    if (error) {
      setErrors(prev => ({ ...prev, attachment: error }));
      return;
    }
    setAttachment(file);
    setErrors(prev => ({ ...prev, attachment: undefined }));
  };

  // Handle drag events
  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Remove attachment
  const removeAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const categories = [
    { value: "", label: "Select a category" },
    { value: "general", label: "General Inquiry" },
    { value: "bug", label: "Bug Report" },
    { value: "account", label: "Account Issue" },
    { value: "feedback", label: "Feedback / Suggestion" },
    { value: "privacy", label: "Privacy / Data Request" },
    { value: "legal", label: "Legal Inquiry" },
    { value: "other", label: "Other" }
  ];

  const websitePages = [
    { value: "", label: "Select a page" },
    { value: "na", label: "N/A" },
    { value: "dashboard", label: "Dashboard" },
    { value: "play", label: "Play" },
    { value: "multiplayer", label: "Multiplayer" },
    { value: "pieces", label: "Pieces" },
    { value: "variants", label: "Variants" },
    { value: "ai-settings", label: "AI Settings" },
    { value: "leaderboard", label: "Leaderboard" },
    { value: "profile", label: "Profile" },
    { value: "documents", label: "Documents" },
    { value: "help", label: "Help" },
    { value: "privacy", label: "Privacy Policy" },
    { value: "terms", label: "Terms of Service" },
    { value: "sign-in", label: "Sign In" }
  ];

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Category validation
    if (!formData.category) {
      newErrors.category = "Please select a category";
    }

    // Website Page validation
    if (!formData.websitePage) {
      newErrors.websitePage = "Please select a page";
    }

    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    } else if (formData.subject.trim().length < 5) {
      newErrors.subject = "Subject must be at least 5 characters";
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 20) {
      newErrors.message = "Message must be at least 20 characters";
    } else if (formData.message.trim().length > 5000) {
      newErrors.message = "Message must not exceed 5000 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const SERVER_URL = process.env.REACT_APP_API_URL || "";

      // Use FormData to support file upload
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('subject', formData.subject);
      submitData.append('category', formData.category);
      submitData.append('websitePage', formData.websitePage);
      submitData.append('message', formData.message);

      // Add attachment if present
      if (attachment) {
        submitData.append('attachment', attachment);
      }

      const response = await fetch(`${SERVER_URL}/api/contact`, {
        method: "POST",
        // Don't set Content-Type header - browser will set it with boundary for FormData
        body: submitData
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus("success");
        setSubmitMessage("Your message has been sent successfully. We will respond within 2-3 business days.");
        setFormData({
          name: "",
          email: "",
          subject: "",
          category: "",
          websitePage: "",
          message: ""
        });
        // Clear attachment
        setAttachment(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setSubmitStatus("error");
        setSubmitMessage(data.error || "Failed to send message. Please try again later.");
      }
    } catch (error) {
      setSubmitStatus("error");
      setSubmitMessage("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <PageHeader
        icon="✉️"
        title="Contact Us"
        description="Get in touch with our support team"
      />

      <div className="contact-content">
        <div className="contact-grid">
          {/* Contact Form */}
          <div className="contact-form-section">
            <div className="form-card">
              <h2 className="form-title">Send Us a Message</h2>
              <p className="form-subtitle">
                Fill out the form below and we will get back to you within 2-3 business days.
              </p>

              {submitStatus === "success" && (
                <div className="alert alert-success">
                  <span className="alert-icon">✓</span>
                  <span>{submitMessage}</span>
                </div>
              )}

              {submitStatus === "error" && (
                <div className="alert alert-error">
                  <span className="alert-icon">✕</span>
                  <span>{submitMessage}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form" noValidate>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">
                      Your Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`form-input ${errors.name ? 'error' : ''}`}
                      placeholder="Enter your name"
                      disabled={isSubmitting}
                    />
                    {errors.name && <span className="error-message">{errors.name}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`form-input ${errors.email ? 'error' : ''}`}
                      placeholder="Enter your email"
                      disabled={isSubmitting}
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="category" className="form-label">
                      Category <span className="required">*</span>
                    </label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className={`form-select ${errors.category ? 'error' : ''}`}
                      disabled={isSubmitting}
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                    {errors.category && <span className="error-message">{errors.category}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="websitePage" className="form-label">
                      Website Page <span className="required">*</span>
                    </label>
                    <select
                      id="websitePage"
                      name="websitePage"
                      value={formData.websitePage}
                      onChange={handleInputChange}
                      className={`form-select ${errors.websitePage ? 'error' : ''}`}
                      disabled={isSubmitting}
                    >
                      {websitePages.map(page => (
                        <option key={page.value} value={page.value}>
                          {page.label}
                        </option>
                      ))}
                    </select>
                    {errors.websitePage && <span className="error-message">{errors.websitePage}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject" className="form-label">
                    Subject <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className={`form-input ${errors.subject ? 'error' : ''}`}
                    placeholder="Brief description of your inquiry"
                    disabled={isSubmitting}
                  />
                  {errors.subject && <span className="error-message">{errors.subject}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    Message <span className="required">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`form-textarea ${errors.message ? 'error' : ''}`}
                    placeholder="Please provide details about your inquiry..."
                    rows={6}
                    disabled={isSubmitting}
                  />
                  <div className="textarea-footer">
                    {errors.message && <span className="error-message">{errors.message}</span>}
                    <span className="char-count">
                      {formData.message.length} / 5000 characters
                    </span>
                  </div>
                </div>

                {/* File Attachment Section */}
                <div className="form-group">
                  <label className="form-label">
                    Attachment <span className="optional">(optional)</span>
                  </label>
                  <div
                    className={`file-drop-zone ${isDragging ? 'dragging' : ''} ${attachment ? 'has-file' : ''} ${errors.attachment ? 'error' : ''}`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => !attachment && fileInputRef.current?.click()}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileInputChange}
                      accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt,.zip"
                      style={{ display: 'none' }}
                      disabled={isSubmitting}
                    />

                    {attachment ? (
                      <div className="file-preview">
                        <div className="file-info">
                          <span className="file-icon">📎</span>
                          <div className="file-details">
                            <span className="file-name">{attachment.name}</span>
                            <span className="file-size">{formatFileSize(attachment.size)}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="remove-file-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeAttachment();
                          }}
                          disabled={isSubmitting}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="drop-zone-content">
                        <span className="drop-icon">📁</span>
                        <p className="drop-text">
                          <strong>Drag and drop</strong> a file here, or <strong>click to browse</strong>
                        </p>
                        <p className="drop-hint">
                          Max 10 MB. Supported: images, PDF, Word, text, ZIP
                        </p>
                      </div>
                    )}
                  </div>
                  {errors.attachment && <span className="error-message">{errors.attachment}</span>}
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <span>📤</span>
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Contact Information Sidebar */}
          <div className="contact-info-section">
            <div className="info-card">
              <h3>📬 Contact Information</h3>
              <div className="info-item">
                <span className="info-icon">✉️</span>
                <div className="info-content">
                  <strong>Email</strong>
                  <a href="mailto:info@expandedchess.com">info@expandedchess.com</a>
                </div>
              </div>
              <div className="info-item">
                <span className="info-icon">📍</span>
                <div className="info-content">
                  <strong>Address</strong>
                  <span>1000 S Hwy 395, Box 125, Hermiston, OR 97838</span>
                </div>
              </div>
            </div>

            <div className="info-card">
              <h3>⏰ Response Times</h3>
              <ul className="response-times">
                <li>
                  <span className="time-label">General Inquiries</span>
                  <span className="time-value">2-3 business days</span>
                </li>
                <li>
                  <span className="time-label">Account Issues</span>
                  <span className="time-value">1-2 business days</span>
                </li>
                <li>
                  <span className="time-label">Bug Reports</span>
                  <span className="time-value">3-5 business days</span>
                </li>
                <li>
                  <span className="time-label">Privacy Requests</span>
                  <span className="time-value">Within 30 days</span>
                </li>
              </ul>
            </div>

            <div className="info-card">
              <h3>📋 Before You Write</h3>
              <p className="info-tip">
                Have you checked our <a href="/help">Help & FAQ</a> section? 
                Many common questions are answered there.
              </p>
              <p className="info-tip">
                For privacy-related requests (data access, deletion, etc.), 
                please select "Privacy / Data Request" as the category.
              </p>
            </div>

            <div className="info-card highlight">
              <h3>🔒 Your Privacy</h3>
              <p>
                Information submitted through this form is used solely to 
                respond to your inquiry. See our{" "}
                <a href="/privacy">Privacy Policy</a> for details.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
